import React, { FC, ReactNode } from "react";
import { useDimensions } from "../../utils/useDimensions";
import { WidgetBorderTheme } from "@xapp/stentor-chat-widget";
import "./CtaBubble.scss";

export interface CtaBubbleProps {
    onClick: () => void;
    readonly borderStyle?: WidgetBorderTheme;
    readonly animate?: boolean;
    readonly buttonAnimation?: string;
    readonly dismissible?: boolean;
    readonly onDismiss?: () => void;
    children?: ReactNode;
}

interface Vec {
    readonly x: number;
    readonly y: number;
}

function add(left: Vec, right: Vec): Vec {
    return { x: left.x + right.x, y: left.y + right.y };
}

function sub(left: Vec, right: Vec): Vec {
    return { x: left.x - right.x, y: left.y - right.y };
}

function mul(val: Vec, k: number): Vec {
    return { x: val.x * k, y: val.y * k };
}

function len(vec: Vec) {
    return Math.sqrt(vec.x * vec.x + vec.y * vec.y);
}

function resize(vec: Vec, length: number): Vec {
    const currentLength = len(vec);
    if (!currentLength) {
        return vec;
    }
    return mul(vec, length / currentLength);
}

function rotateZ(vec: Vec, angle: number): Vec {
    const rad = (angle * Math.PI) / 180;
    const ca = Math.cos(rad);
    const sa = Math.sin(rad);
    const resx = vec.x * ca + vec.y * sa;
    const resy = vec.y * ca - vec.x * sa;
    return { x: resx, y: resy };
}

function intersectLineEllipsis(
    ellipsis: {
        center: Vec;
        radius: Vec;
    },
    line: {
        start: Vec;
        direction: Vec;
    }
) {
    const lineStartRel = sub(line.start, ellipsis.center);
    const sx = lineStartRel.x;
    const sy = lineStartRel.y;
    const kx = line.direction.x;
    const ky = line.direction.y;
    const rx = ellipsis.radius.x;
    const ry = ellipsis.radius.y;

    const rx2 = rx * rx;
    const ry2 = ry * ry;

    const a = (kx * kx) / rx2 + (ky * ky) / ry2;
    const b = (2 * kx * sx) / rx2 + (2 * ky * sy) / ry2;
    const c = (sx * sx) / rx2 + (sy * sy) / ry2 - 1;

    const d2 = b * b - 4 * a * c;
    if (d2 >= 0) {
        const d = Math.sqrt(d2);
        const t1 = (-b - d) / (2 * a);
        const t2 = (-b + d) / (2 * a);

        if (t1 > 0 && t2 > 0) {
            return add(line.start, mul(line.direction, Math.min(t1, t2)));
        }
        if (t1 > 0) {
            return add(line.start, mul(line.direction, t1));
        }
        if (t2 > 0) {
            return add(line.start, mul(line.direction, t2));
        }
    }

    return line.start;
}

function getTailSvgPath(props: CtaBubbleTailProps, viewport: Vec): string {
    const baseAngleRad = (props.direction * Math.PI) / 180;
    const radius: Vec = { x: props.width / 2, y: props.height / 2 };
    const center: Vec = { x: viewport.x / 2, y: viewport.y / 2 };

    const intersectionRel: Vec = {
        x: radius.x * Math.cos(baseAngleRad),
        y: radius.y * Math.sin(baseAngleRad),
    };
    const farRel = resize(intersectionRel, len(intersectionRel) + props.length);
    const far = add(farRel, center);
    const direction = sub(intersectionRel, farRel);
    const angle = props.angle;
    const leftDirection = rotateZ(direction, angle / 2);
    const rightDirection = rotateZ(direction, -angle / 2);

    const ellipsis = {
        center,
        radius,
    };

    const left = intersectLineEllipsis(ellipsis, {
        start: far,
        direction: leftDirection,
    });

    const right = intersectLineEllipsis(ellipsis, {
        start: far,
        direction: rightDirection,
    });

    return `m ${far.x} ${far.y} L ${left.x} ${left.y} L ${right.x} ${right.y} z`;
}

export interface CtaBubbleTailProps {
    readonly width: number;
    readonly height: number;
    readonly direction: number;
    readonly angle: number;
    readonly length: number;
}
export const CtaBubbleTail: FC<CtaBubbleTailProps> = (props) => {
    const [ref, rect] = useDimensions<HTMLDivElement>();
    const viewPort: Vec = { x: rect?.width ?? 0, y: rect?.height ?? 0 };

    return (
        <div ref={ref} className="cta-bubble__tail">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${viewPort.x} ${viewPort.y}`}>
                <path d={getTailSvgPath(props, viewPort)} fill="currentColor" />
            </svg>
        </div>
    );
};

const CtaBubble: FC<React.PropsWithChildren<CtaBubbleProps>> = (props) => {
    const [ref, rect] = useDimensions<HTMLDivElement>();

    const animate = props.animate || false;

    const animation = animate && props.buttonAnimation === "bounce" ? "bounce" : "none";

    const handleDismiss = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (props.onDismiss) {
            props.onDismiss();
        }
    };

    console.log("Returning CTABubble with message: ", props.children);

    return (
        <div
            ref={ref}
            style={{
                border: props.borderStyle ? "solid" : "none",
                borderWidth: props.borderStyle?.width || "0px",
                borderColor: props.borderStyle?.color || "transparent",
                animation: `${animation} 1s infinite`,
            }}
            className="cta-bubble"
            onClick={props.onClick}>
            <CtaBubbleTail
                width={rect?.width ?? 0}
                height={rect?.height ?? 0}
                direction={60}
                angle={45}
                length={16}
            />
            <div className="cta-bubble__content">{props.children}</div>
            {props.dismissible && (
                <button
                    className="cta-bubble__dismiss"
                    onClick={handleDismiss}
                    aria-label="Dismiss">
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M11 1L1 11M1 1L11 11"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default CtaBubble;
